import { EmojiList } from '@mm/components';
import { activeChatbotSelector } from '@mm/state/selectors/viewSelectors';
import { usePromoterCRUDActions } from '@mm/state/actions/PromoterCRUDActions';
import { useStoryReactionsKeywordAlreadyExists } from '@mm/state/hooks/use-story-reactions-keywords';
import { PromoterModel } from '@mm/state/model/PromoterModel';
import { InfoIcon } from '@mm/icons';
import closeIcon from 'assets/icons/close.svg';
import React, { useCallback, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import classNames from 'classnames';
import KeywordsForm from '../../../startBotPromoters/commentGuards/components/EditFBPost/KeywordsForm/KeywordsForm';
import RadioInput from '../../../../../common/components/RadioInput';
import styles from './InstagramRespondRadioBtns.module.css';

type Props = {
  promoter: PromoterModel;
  promoters: PromoterModel[];
  type: 'mentions' | 'ig_live';
  customRespondToAllLabel?: string;
  customRespondKeywordsLabel?: string;
  customRespondEmojisLabel?: string;
  css?: {
    radioInputLabel?: string;
    textLink?: string;
  };
  showInfoIcon?: boolean;
  keywordInfoIconColor?: string;
  customKeywordBroadPosition?: 'top' | 'bottom' | 'right' | 'left';
  customKeywordBroadAlign?: 'start' | 'center' | 'end';
};

export const InstagramRespondRadioBtns = (props: Props) => {
  const {
    promoter,
    promoters,
    type,
    customRespondToAllLabel,
    customRespondKeywordsLabel,
    customRespondEmojisLabel,
    css,
    showInfoIcon,
    keywordInfoIconColor,
    customKeywordBroadPosition,
    customKeywordBroadAlign,
  } = props;
  const { existsEmoji, getKeywordsForPromoter, getEmojisForPromoter } =
    useStoryReactionsKeywordAlreadyExists(promoters, type, promoter);
  const keywords = promoter ? getKeywordsForPromoter(promoter) : [];
  const emojis = promoter ? getEmojisForPromoter(promoter) : [];
  const [selectedEmojis, setSelectedEmojis] = useState<Set<string>>(
    new Set([]),
  );
  const [isQuickReactions, setIsQuickReactions] = useState(!!emojis.length);
  const isQuickReactionsEnabled = !!(
    promoter?.keywordsEnabled && isQuickReactions
  );
  const [respondTo, setReaspondTo] = useState<
    'all' | 'quickReactions' | 'keywords'
  >(
    !promoter?.keywordsEnabled
      ? 'all'
      : isQuickReactionsEnabled
      ? 'quickReactions'
      : 'keywords',
  );

  const chatbot = useSelector(activeChatbotSelector);

  const promoterCRUDActions = usePromoterCRUDActions();

  useEffect(() => {
    setSelectedEmojis(new Set(emojis));
    setReaspondTo(
      isQuickReactionsEnabled || emojis.length
        ? 'quickReactions'
        : !promoter?.keywordsEnabled
        ? 'all'
        : 'keywords',
    );
  }, [promoter]);

  const setKeywordsEnabled = useCallback(
    async (keywordsEnabled: boolean, isQuickReactions: boolean) => {
      if (chatbot?.id) {
        try {
          await promoterCRUDActions.update(
            chatbot.id,
            {
              ...promoter,
              keywordsEnabled,
              keywords: keywordsEnabled ? ['keyword'] : [],
            },
            true,
            true,
          );

          setSelectedEmojis(new Set([]));
          setIsQuickReactions(isQuickReactions);
          setReaspondTo(
            !keywordsEnabled
              ? 'all'
              : isQuickReactions
              ? 'quickReactions'
              : 'keywords',
          );
        } catch {
          // do nothing
        }
      }
    },
    [promoter],
  );

  const updateEmojiKeywords = async (emojis: Set<string>) => {
    const updatedPromoter = {
      ...promoter,
      keywords: [...emojis],
      keywordsEnabled: true,
    };
    setSelectedEmojis(new Set([...emojis]));
    await promoterCRUDActions.update(chatbot?.id, updatedPromoter);
  };

  return (
    <>
      <div className={styles.block}>
        <RadioInput
          value="all"
          className={styles.radio}
          checked={respondTo === 'all'}
          label={customRespondToAllLabel || 'Catch All'}
          onClick={() => setKeywordsEnabled(false, false)}
          css={{
            labelTitle: classNames(styles.label_title, css?.radioInputLabel),
          }}
        />
        {!promoter?.keywordsEnabled && (
          <div className={classNames(styles.text_link, css?.textLink)}>
            Catch-all: will reply to all{' '}
            {type === 'ig_live' ? 'live comments' : 'reactions'} that don't
            match other keywords
            {showInfoIcon && (
              <InfoIcon
                className="comment-guard-keywords__broad-match-info-icon"
                color="#262626"
              />
            )}
          </div>
        )}
      </div>

      <div className={styles.block}>
        <RadioInput
          value="keywords"
          className={styles.radio}
          checked={respondTo === 'keywords'}
          label={customRespondKeywordsLabel || 'If user reply with keywords'}
          onClick={() => setKeywordsEnabled(true, false)}
          css={{
            labelTitle: classNames(styles.label_title, css?.radioInputLabel),
          }}
        />
        {respondTo === 'keywords' && (
          <KeywordsForm
            guard={promoter}
            useUpdatedDesign
            allowPhraseKeywords
            showDisabledState
            showKeywordsBroadMatch
            inputPlaceholder="e.g. “Home”"
            customAddBtnName="Apply"
            customKeywords={keywords}
            customDeleteKeywordIcon={
              <img src={closeIcon} alt="" className={styles.deleteIcon} />
            }
            infoIconColor={keywordInfoIconColor}
            customKeywordBroadPosition={customKeywordBroadPosition}
            customKeywordBroadAlign={customKeywordBroadAlign}
          />
        )}
      </div>
      <div className={styles.block}>
        <RadioInput
          value="quickReactions"
          className={styles.radio}
          checked={respondTo === 'quickReactions'}
          label={customRespondEmojisLabel || 'Respond to Quick Reactions'}
          onClick={() => setKeywordsEnabled(true, true)}
          css={{
            labelTitle: classNames(styles.label_title, css?.radioInputLabel),
          }}
        />
        {respondTo === 'quickReactions' && (
          <EmojiList
            selectedEmojis={selectedEmojis}
            invalidEmojis={existsEmoji}
            setSelectedEmojis={emojis => updateEmojiKeywords(emojis)}
            tooltipText="This emoji is already in use in the other starter"
            isMM
          />
        )}
      </div>
    </>
  );
};
